import { SseDecoder, SSE_EVENT_NAMES, type ModelCatalogItem, type MrbdSseEvent, type SessionResponse } from "@mrbd/contracts";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code = "REQUEST_FAILED", status = 0) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const body = await response.json() as { error?: { code?: string; message?: string } };
    return new ApiError(body.error?.message ?? `Request failed (${response.status}).`, body.error?.code, response.status);
  } catch {
    return new ApiError(`Request failed (${response.status}).`, "HTTP_ERROR", response.status);
  }
}

export async function getSession(signal?: AbortSignal): Promise<SessionResponse> {
  const response = await fetch("/api/session", { credentials: "same-origin", ...(signal ? { signal } : {}) });
  if (response.status === 401) {
    const body = await response.json() as SessionResponse;
    return body;
  }
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<SessionResponse>;
}

export async function getModels(signal?: AbortSignal): Promise<readonly ModelCatalogItem[]> {
  const response = await fetch("/api/models", { credentials: "same-origin", ...(signal ? { signal } : {}) });
  if (!response.ok) throw await readError(response);
  const body = await response.json() as { models?: ModelCatalogItem[] };
  if (!Array.isArray(body.models)) throw new ApiError("The server model catalog is invalid.", "MODEL_CATALOG_INVALID", response.status);
  return body.models;
}

export async function redeemPairing(code: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch("/api/pairing/redeem", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw await readError(response);
}

export async function logout(csrfToken: string): Promise<void> {
  const response = await fetch("/api/session/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-csrf-token": csrfToken },
  });
  if (!response.ok) throw await readError(response);
}

export interface DiagnosticEvent {
  event: "recording-start" | "recording-stop" | "recorder-data" | "lifecycle" | "network";
  pageInstanceId: string;
  mimeType?: string;
  audioBytes?: number;
  dataAvailable?: boolean;
  stopEvent?: boolean;
  errorName?: string;
  lifecycle?: "blur" | "hidden" | "visible" | "pagehide" | "focus";
  online?: boolean;
}

export async function sendDiagnostics(csrfToken: string, events: readonly DiagnosticEvent[], signal?: AbortSignal): Promise<void> {
  if (!events.length) return;
  const response = await fetch("/api/diagnostics", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
    body: JSON.stringify({ events }),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw await readError(response);
}

export interface ContextTurn {
  question: string;
  answer: string;
}

export interface TurnRequest {
  audio?: Blob;
  transcript?: string;
  durationMs: number;
  pageInstanceId: string;
  csrfToken: string;
  context: readonly ContextTurn[];
  signal: AbortSignal;
  onEvent: (event: MrbdSseEvent) => void;
  onUnknownEvent?: (name: string) => void;
}

function isMrbdEvent(name: string): name is MrbdSseEvent["event"] {
  return (SSE_EVENT_NAMES as readonly string[]).includes(name);
}

export async function streamTurn(request: TurnRequest): Promise<void> {
  const form = new FormData();
  if (request.audio) form.append("audio", request.audio, `recording.${extensionForMime(request.audio.type)}`);
  if (request.transcript) form.append("transcript", request.transcript);
  form.append("durationMs", String(request.durationMs));
  form.append("pageInstanceId", request.pageInstanceId);
  form.append("context", JSON.stringify(request.context.slice(-6)));

  const response = await fetch("/api/turns", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-csrf-token": request.csrfToken },
    body: form,
    signal: request.signal,
  });
  if (!response.ok) throw await readError(response);
  if (!response.body) throw new ApiError("Streaming response body is unavailable.", "STREAM_UNAVAILABLE", response.status);

  await readSse(response, request.onEvent, request.onUnknownEvent);
}

async function readSse(response: Response, onEvent: (event: MrbdSseEvent) => void, onUnknownEvent?: (name: string) => void): Promise<void> {
  if (!response.body) throw new ApiError("Streaming response body is unavailable.", "STREAM_UNAVAILABLE", response.status);
  const decoder = new TextDecoder();
  const sse = new SseDecoder();
  const reader = response.body.getReader();
  const dispatch = (items: ReturnType<SseDecoder["push"]>): void => {
    for (const item of items) {
      if (!isMrbdEvent(item.event)) {
        onUnknownEvent?.(item.event);
        continue;
      }
      let event: MrbdSseEvent;
      try {
        event = { event: item.event, ...JSON.parse(item.data) } as MrbdSseEvent;
      } catch {
        throw new ApiError("The server sent an invalid streaming event.", "INVALID_SSE", response.status);
      }
      onEvent(event);
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      dispatch(sse.push(decoder.decode(value, { stream: true })));
    }
    dispatch(sse.push(decoder.decode()));
    dispatch(sse.finish());
  } finally {
    reader.releaseLock();
  }
}

function extensionForMime(mime: string): string {
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("wav")) return "wav";
  return "webm";
}
